import type { CSSProperties, ReactNode } from 'react';
import cometChatLogo from '../../assets/cometchat_logo_dark.svg';
import './LoginBrand.css';

/**
 * The brand side of the split sign-in, modelled on CometChat Air's _BrandPanel
 * (showcases/flutter/app/lib/features/auth/login_screen.dart).
 *
 * Air lists "how it works" as numbered steps because that IS a sequence. These
 * are capabilities, which have no order, so each gets an icon tile instead of a
 * number -- a number would claim a sequence that does not exist.
 *
 * Every item is backed by code in this app: meetings (Home/JoinSession), screen
 * share, virtual backgrounds + noise suppression (the VB worker), and in-call
 * chat (ChatDemo) are open to anyone who signs in. Recording and transcription
 * are for @cometchat.com accounts only (isCometChatAccount), so their entry says
 * so in its own text rather than promising a public user something they can't
 * use (owner, 2026-10-20). Picture-in-picture is absent: nothing implements it.
 *
 * Rendered once. Below 900px the panel dissolves: the head sits on the page
 * above the form with no band, and the list gives way to a one-line summary at
 * the foot -- stacked, the full panel turned a phone into a scrolling page of
 * boxes. Android's phone layout makes the same cut.
 *
 * The wordmark is white-only, and below 900px it sits on the page instead of
 * the band, so it is drawn as a mask filled with the hero colour rather than as
 * an <img>; otherwise it would vanish on a light page.
 */

type Capability = { icon: ReactNode; title: string; text: string };

const Icon = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const CAPABILITIES: Capability[] = [
  {
    icon: <Icon><rect x="2.5" y="5" width="10.5" height="10" rx="2" /><path d="m13 9 4.5-2.5v7L13 11" /></Icon>,
    title: 'Voice and video meetings',
    text: 'One-to-one or with the whole group, right in the browser.',
  },
  {
    icon: <Icon><rect x="2.5" y="3.5" width="15" height="10" rx="1.5" /><path d="M7 17h6M10 13.5V17M10 10.5V6.5m-2 2 2-2 2 2" /></Icon>,
    title: 'Screen sharing',
    text: 'Present your screen while everyone stays in the call.',
  },
  {
    icon: <Icon><path d="M10 2.5v2m0 11v2m7.5-7.5h-2m-11 0h-2m12.3-5.3-1.4 1.4m-7.8 7.8-1.4 1.4m10.6 0-1.4-1.4M6.2 6.2 4.8 4.8" /><circle cx="10" cy="10" r="3" /></Icon>,
    title: 'Virtual backgrounds and noise suppression',
    text: 'Tidy up what others see and hear.',
  },
  {
    icon: <Icon><path d="M4 4.5h12a1.5 1.5 0 0 1 1.5 1.5v6.5a1.5 1.5 0 0 1-1.5 1.5H9l-3.5 3v-3H4a1.5 1.5 0 0 1-1.5-1.5V6A1.5 1.5 0 0 1 4 4.5Z" /></Icon>,
    title: 'In-call chat',
    text: 'Message the room without talking over anyone.',
  },
  {
    icon: <Icon><circle cx="10" cy="10" r="7.5" /><circle cx="10" cy="10" r="3" fill="currentColor" /></Icon>,
    title: 'Recording and transcription',
    text: 'Record a call and get a transcript afterwards. For CometChat accounts.',
  },
];

export function LoginBrand() {
  return (
    <div className="login-brand">
      <div className="login-brand__head">
        <span
          className="login-brand__logo"
          role="img"
          aria-label="CometChat"
          style={{ '--login-logo': `url(${cometChatLogo})` } as CSSProperties}
        />
        <div className="login-brand__intro">
          <p className="login-brand__eyebrow">CometChat Calls</p>
          <h1 className="login-brand__headline">
            Voice and video calls,
            <br />
            right in your browser.
          </h1>
        </div>
      </div>

      <div className="login-brand__list">
        <p className="login-brand__label" id="login-capabilities">What you can do</p>
        <ul className="login-brand__items" aria-labelledby="login-capabilities">
          {CAPABILITIES.map(c => (
            <li className="login-brand__item" key={c.title}>
              <span className="login-brand__tile">{c.icon}</span>
              <span className="login-brand__copy">
                <span className="login-brand__title">{c.title}</span>
                <span className="login-brand__text">{c.text}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="login-brand__foot">Built on the CometChat Calls SDK.</p>
      </div>

      {/* Below 900px only, in place of the list above. */}
      <p className="login-brand__summary">
        Meetings · Screen sharing · Backgrounds · Chat
      </p>
    </div>
  );
}
